import { Button, Flex, Space, Spin, Table, type TableProps } from 'antd'
import { useState } from 'react'

import { FeatureFlagRuleService } from '@services/FeatureFlagRuleService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { FeatureFlag, FeatureFlagRule } from '../../../types'

import { AddFeatureFlagRuleModal } from './components/AddFeatureFlagRuleModal'

type FeatureFlagRulesTabProps = {
    featureFlags: FeatureFlag[] | undefined
    featureFlagRules: FeatureFlagRule[] | undefined
    refetchRules: () => void
}

export const FeatureFlagRulesTab = ({ featureFlags, featureFlagRules, refetchRules }: FeatureFlagRulesTabProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false)

    const confirm = useConfirmation()

    const { showNotification } = useNotifications()

    const onAddRule = async (formValues: Partial<FeatureFlagRule>) => {
        await FeatureFlagRuleService.create(formValues)
        await refetchRules()
    }

    const columns: TableProps<FeatureFlagRule>['columns'] = [
        {
            title: 'Rule Id',
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: 'Feature Flag',
            dataIndex: 'featureFlagKey',
            key: 'key',
            sorter: (a, b) => {
                if (!a?.featureFlag?.key || !b?.featureFlag?.key) {
                    return 1
                } else {
                    return a.featureFlag.key.localeCompare(b.featureFlag.key)
                }
            }
        },
        {
            title: 'Model',
            dataIndex: 'model',
            key: 'model',
            sorter: (a, b) => a.model.localeCompare(b.model)
        },
        {
            title: 'Field',
            dataIndex: 'field',
            key: 'field',
            sorter: (a, b) => a.field.localeCompare(b.field)
        },
        {
            title: 'Comparator',
            dataIndex: 'comparator',
            key: 'comparator',
            sorter: (a, b) => a.comparator.localeCompare(b.comparator)
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            sorter: (a, b) => a.value.localeCompare(b.value)
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        type="link"
                        onClick={async () => {
                            if (
                                await confirm({
                                    title: 'Are you sure you want to delete this rule?',
                                    confirmText: 'Remove'
                                })
                            ) {
                                try {
                                    await FeatureFlagRuleService.delete(record.id)
                                    await refetchRules()
                                    showNotification({ title: 'Feature flag rule removed', type: 'success' })
                                } catch (error) {
                                    showNotification({ title: 'Error removing feature flag rule', type: 'error' })
                                }
                            }
                        }}
                    >
                        Delete
                    </Button>
                </Space>
            )
        }
    ]

    if (!featureFlags || !featureFlagRules) return <Spin />

    return (
        <>
            <Button onClick={() => setIsModalOpen(true)}>Add New Rule</Button>
            <Flex gap="middle" vertical>
                <Table
                    columns={columns}
                    dataSource={featureFlagRules.map(u => {
                        return {
                            ...u,
                            featureFlagKey: u.featureFlag?.key
                        }
                    })}
                    rowKey="id"
                    pagination={false}
                />
            </Flex>
            <AddFeatureFlagRuleModal
                featureFlags={featureFlags}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAddRule={onAddRule}
            />
        </>
    )
}
